import { useQuery } from '@tanstack/react-query'
import { Suspense } from 'react'
import { NavLink, Outlet } from 'react-router-dom'

import { api } from '../api/endpoints'
import { formatDate, roleLabel } from '../format'
import { useAuth } from '../hooks/useAuth'
import { Loading } from './StateViews'

const NAV_ITEMS = [
  { to: '/', label: 'Riepilogo', exact: true },
  { to: '/movements', label: 'Movimenti' },
  { to: '/companies', label: 'Compagnie' },
  { to: '/search', label: 'Ricerca' },
]

function navClass({ isActive }: { isActive: boolean }): string {
  // shrink-0 because the row scrolls sideways on a narrow screen: without it
  // flex would compress the labels instead of letting them run off the edge.
  return `shrink-0 rounded-md px-3 py-1.5 text-sm ${
    isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
  }`
}

/** The red band on top of every page when ingestion has stopped. */
function DelayBanner() {
  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: ({ signal }) => api.health(signal),
    refetchInterval: 5 * 60 * 1000,
  })
  if (!health || health.status === 'ok') return null
  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-rose-600 px-4 py-2 text-center text-sm font-medium text-white"
    >
      {health.status === 'empty'
        ? 'Nessuno snapshot applicato: l’archivio è vuoto.'
        : `Ultimo snapshot applicato il ${formatDate(health.last_snapshot)} — ` +
          `${Math.round(health.delay_hours ?? 0)} ore fa, oltre la soglia di ${health.threshold_hours}. ` +
          'Ogni notte persa è storia che non si recupera.'}
    </div>
  )
}

export function Layout() {
  const { profile, logout, hasRole } = useAuth()

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <DelayBanner />
      {/* One row on a desktop, as before. On a phone the five sections plus an
          address cannot share a line, so the navigation drops below and scrolls
          sideways on its own instead of widening the page. */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
          <span className="text-sm font-semibold tracking-tight">Movimenti RUI</span>

          <nav
            // -mx-4 px-4 lets the scrolling row bleed to the screen edges, so the
            // last section is not clipped mid-word against the page padding.
            className="order-last -mx-4 flex w-full gap-1 overflow-x-auto px-4 sm:order-none sm:mx-0 sm:w-auto sm:overflow-x-visible sm:px-0"
            aria-label="Sezioni"
          >
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.exact} className={navClass}>
                {item.label}
              </NavLink>
            ))}
            {hasRole('admin') && (
              <NavLink to="/system" className={navClass}>
                Sistema
              </NavLink>
            )}
          </nav>

          <div className="ml-auto flex items-center gap-3 text-sm text-slate-500">
            {/* The address is the longest thing here and the least useful when
                there is one account; the role and the way out are what matter. */}
            <span className="hidden truncate sm:inline">
              {profile?.email} · {profile ? roleLabel(profile.role) : ''}
            </span>
            <span className="sm:hidden">{profile ? roleLabel(profile.role) : ''}</span>
            <button
              type="button"
              onClick={() => void logout()}
              className="text-slate-600 underline hover:text-slate-900"
            >
              esci
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        {/* One boundary for every lazily loaded page underneath. */}
        <Suspense fallback={<Loading rows={6} />}>
          <Outlet />
        </Suspense>
      </main>

      <footer className="mx-auto max-w-7xl px-4 pb-8 text-xs text-slate-400">
        Fonte: IVASS, Registro Unico degli Intermediari, dati aperti — elaborazione propria. Le date
        sono quelle di rilevazione nel registro, non quelle di efficacia dell’atto.
      </footer>
    </div>
  )
}
